import { call, put, select, takeEvery, takeLatest, debounce } from 'redux-saga/effects';
import { matchPath } from 'connected-react-router';
import { LOCATION_CHANGE, push, goBack, getLocation } from 'connected-react-router';
import { actions, constants, selector } from './redux';
import Api from '../../utils/Api';
import {addError, addSuccess} from "../notifications/actions";
import {routeConfig} from "../vessel-details/redux";
import { createUrlFilterSagas } from '../../utils/redux/urlFilterSagas';
import { getFilterByLocationSearch, getQueryByFilter, withLabelsFrom } from './urlFilters';

// CREW-1024 — the query string is the single source of truth for the list
// filter, the contract /vacancies (CREW-1169) and /task-manager (CREW-1040)
// already follow:
//   URL --getFilterByLocationSearch--> filter  (on LOCATION_CHANGE)
//   filter --getQueryByFilter--> URL           (on APPLY_FILTER)
// APPLY_FILTER therefore no longer fetches by itself: it writes the URL, and
// the LOCATION_CHANGE that follows hydrates the filter and fetches once.
export const basePageUrl = '/vessels';

export const { pageInit, syncFilterToUrl } = createUrlFilterSagas({
  basePageUrl,
  selectFeature: (state) => state.vessels,
  urlFilters: { getFilterByLocationSearch, getQueryByFilter, withLabelsFrom },
  actions,
});

// Per-field error display on submit failures used to come from
// redux-form's `stopSubmit('vessels', errorByField)` action. After
// the RHF migration in Epic 13e, the snackbar at the top of the
// page is the only error surface. The errorByField map is still
// attached to the thrown error (Api.handleError) — wire it back
// to RHF's `setError` when needed (Epic 13e follow-up).



export function* fetchItem() {
  try {
    const { pathname } = yield select(getLocation);
    const { params: { id } } = matchPath(pathname, routeConfig);
    const featureState = yield select(selector);
    const data = yield call(Api.vessels.show, id, featureState);

    yield put(actions.setItem(data));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemError(e));
  }
}


export function* fetchItems() {
  try {
    const featureState = yield select(selector);
    const { data: json } = yield call(Api.vessels.index, featureState);

    yield put(actions.addItems(json));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemsError(e));
  }
}

// Typing in the search box goes through the URL like every other filter: reset
// the page, then write the query string and let the resulting LOCATION_CHANGE
// hydrate and fetch. One code path, and what is on screen is always what the
// address bar says.
//
// Opening a link that already carries `?search=…` hydrates the box, which trips
// this watcher once 700ms later; `syncFilterToUrl` sees the identical query and
// refetches instead of navigating. One extra request on that first open, and no
// way for the two to disagree — the trade the old localStorage search lost.
function* fetchAfterSearch() {
  yield put(actions.applySearch()); // reset current pagination page

  yield* syncFilterToUrl();
}

function* createItem({ payload }) {
  try {
    const { data: { data } } = yield call(Api.vessels.create, payload);
    yield put(push(`/vessels/${data.id}/details`));

    yield put(addSuccess('Vessel created successfully'));
  } catch (e) {
    yield put(actions.createItemError(e));
  }
}

function* updateItem({ payload }) {
  try {
    const { pathname } = yield select(getLocation);
    const { params: { id } } = matchPath(pathname, { path: "/vessels/:id/:tab?/edit" });

    const { data: { data } } = yield call(Api.vessels.update, id, payload);
    // QA 1.2 round 2 (2026-05-25): mirror the Cancel-side symmetry
    // — the round-1 fix made Cancel call `history.goBack()` so the
    // user lands back where they opened the form from (list or
    // detail page), but Save still hard-coded a `push()` to the
    // detail page. From the list, Save would unexpectedly leave
    // the user on the detail page instead of returning to the
    // list. `goBack()` matches the Cancel path; the row inside
    // `state.data` is refreshed via the `updateItem` dispatch
    // below so the list view reflects the saved change without
    // a hard refresh (see QA 3.2/2026-05-12 in CRUD-sagas).
    if (data) yield put(actions.updateItem(data));
    yield put(goBack());

    yield put(addSuccess('Vessel updated successfully'));
  } catch (e) {
    console.error(e);
    yield put(actions.updateItemError(e));
  }
}

function* addImosToParse({ payload: { imos, onlyNew, cb } }) {
  try {
    const currentUser = yield select((s) => s.auth.data);
    yield call(Api.equasisVesselSearch.addImosToParse, {imos, email: currentUser.equasis_email, only_new: onlyNew});
    if (cb) yield call(cb);
    yield put(addSuccess('IMOs added to parse successfully'));
  } catch (e) {
    console.error(e);
    yield put(addError('Error while adding IMOs to parse'));
  }
}

/**
 * getFeatureSagas (rename)
 * each saga file should return all feature sagas
 * @returns {ForkEffect[]}
 */
export default function getFeatureSagas() {
  return [
    // takeLatest, as in the CRUD kit: a fast page/sort click must cancel the
    // previous in-flight fetch, or an older response can land last and show
    // the previous page's rows. APPLY_FILTER is absent on purpose — it goes
    // through the URL (see the top of the file).
    takeLatest([
      constants.SORT_ITEMS,
      constants.CHANGE_PAGE_NUMBER,
      constants.CHANGE_PAGE_ITEMS_COUNT,
      constants.LOAD_ITEMS_REQUEST,
    ], fetchItems),
    takeEvery(constants.LOAD_ITEM_REQUEST, fetchItem),
    takeEvery(constants.APPLY_FILTER, syncFilterToUrl),
    takeEvery(LOCATION_CHANGE, pageInit),
    debounce(700, constants.CHANGE_SEARCH, fetchAfterSearch),
    // takeEvery(constants.CREATE_ITEM_REQUEST, createItem),
    takeEvery(constants.UPDATE_ITEM_REQUEST, updateItem),
    takeEvery(constants.ADD_IMOS_TO_PARSE, addImosToParse),

  ];
}
